import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { fetchTeamAnalytics } from '../../api/teamAnalytics';
import { fetchSeasonStats } from '../../api/seasonStats';
import DashboardHeader from './DashboardHeader';
import DashboardOverviewCards from './DashboardOverviewCards';
import DashboardWinLossChart from './DashboardWinLossChart';
import DashboardPitchingBatting from './DashboardPitchingBatting';
import DashboardSplits from './DashboardSplits';
import DashboardRecentResults from './DashboardRecentResults';

const TABS = [
  { id: 'overview', label: 'Overview' },
  { id: 'stats', label: 'Team Stats' },
  { id: 'splits', label: 'Home/Away' },
  { id: 'results', label: 'Results' },
];

const TeamPerformanceDashboard = ({ standings }) => {
  const [analytics, setAnalytics] = useState(null);
  const [seasonStats, setSeasonStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');

  const load = async () => {
    setLoading(true);
    try {
      const [analyticsData, statsData] = await Promise.all([
        fetchTeamAnalytics(111),
        fetchSeasonStats(111),
      ]);
      setAnalytics(analyticsData);
      setSeasonStats(statsData);
      setLastUpdated(new Date());
    } catch (e) {
      console.error('Dashboard fetch failed', e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  return (
    <div className="border-2 border-[#658d71] bg-[rgba(0,0,0,0.05)] w-full">
      <div className="p-3 sm:p-5 border-b-2 border-[#658d71]">
        <DashboardHeader loading={loading} lastUpdated={lastUpdated} onRefresh={load} />
      </div>

      {/* Tabs */}
      <div className="flex border-b-2 border-[#658d71] overflow-x-auto">
        {TABS.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`px-4 py-2.5 text-[10px] sm:text-xs font-bold uppercase tracking-widest whitespace-nowrap border-r border-[#658d71] last:border-0 transition-colors ${activeTab === tab.id ? 'bg-[#386646] text-[#d8ceb3]' : 'text-[#a9c9b1] hover:text-white hover:bg-[rgba(255,255,255,0.04)]'}`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      <div className="p-3 sm:p-5">
        {loading ? (
          <div className="flex items-center justify-center py-16 gap-3">
            <div className="w-7 h-7 border-2 border-[#658d71] border-t-[#d8ceb3] rounded-full animate-spin" />
            <span className="text-[#a9c9b1] text-xs tracking-widest uppercase">Loading dashboard...</span>
          </div>
        ) : (
          <AnimatePresence mode="wait">
            <motion.div
              key={activeTab}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.18 }}
              className="flex flex-col gap-4"
            >
              {activeTab === 'overview' && (
                <>
                  <DashboardOverviewCards allGames={analytics?.allGames} standings={standings} />
                  <DashboardWinLossChart allGames={analytics?.allGames} />
                </>
              )}
              {activeTab === 'stats' && (
                <DashboardPitchingBatting teamStats={seasonStats?.teamStats} />
              )}
              {activeTab === 'splits' && (
                <DashboardSplits allGames={analytics?.allGames} />
              )}
              {activeTab === 'results' && (
                <DashboardRecentResults allGames={analytics?.allGames} />
              )}
            </motion.div>
          </AnimatePresence>
        )}
      </div>
    </div>
  );
};

export default TeamPerformanceDashboard;